<template>
  <section id="accesses-panel">
    <b-loading
      :is-full-page="true"
      :active="isLoading"
      :can-cancel="false"
    ></b-loading>

    <div class="page-inner">
      <div class="content-panel">
        <accesses-title
          :accessesQty="accessesQty"
          :storeId="(storeBeingManaged && storeBeingManaged._id) || ''"
          :storeName="
            (storeBeingManaged &&
              storeBeingManaged.storeInfo &&
              storeBeingManaged.storeInfo.name) ||
            ''
          "
          @get-accesses="getAccesses"
          @update-dates="updateDates"
          @update-store="updateStore"
          @download-accesses-report="downloadAccessesReport"
          @update-accesses-filter="updateAccessesFilter"
        />

        <h2 v-if="!fetched" class="empty-state">
          Clique em Buscar para ver os acessos.
        </h2>

        <template v-else>
          <b-pagination
            v-show="accessesQty > 0"
            class="accesses-pagination"
            :current.sync="current"
            :total="accessesQty"
            :per-page="perPage"
            order="is-centered"
            aria-next-label="Próxima página"
            aria-previous-label="Página anterior"
            aria-page-label="Página"
            aria-current-label="Página atual"
            @change="changePage"
          ></b-pagination>

          <form
            v-show="accessesQty > 0"
            class="go-to-page"
            @submit.prevent="goToPage"
          >
            <span class="accesses-count">
              {{ accessesQty }} acesso{{ accessesQty === 1 ? "" : "s" }}
            </span>
            <span class="go-to-page-controls">
              <span>Ir para página</span>
              <b-input
                v-model.number="pageInput"
                type="number"
                min="1"
                :max="totalPages"
              ></b-input>
              <span>de {{ totalPages }}</span>
              <button class="button" type="submit">Ir</button>
            </span>
          </form>

          <h2 v-if="accesses.length === 0" class="empty-state">
            Ainda não existem acessos :(
          </h2>

          <template v-else>
            <div
              v-if="
                !permissions.canSeeUserPersonalId &&
                !permissions.canSeeUserEmail &&
                !permissions.canSeeUserCellphone
              "
              class="lgpd-notice"
            >
              Em concordância com a Lei 13.709 - Lei Geral de Proteção de Dados
              Pessoais (LGPD) - e em respeito à privacidade dos nossos usuários,
              não são exibidas aqui informações pessoais.
            </div>

            <accesses-table :accesses="accesses" />
          </template>
        </template>
      </div>
    </div>
  </section>
</template>

<script>
import { common } from "../../mixins/common";
import { mapGetters } from "vuex";
import accessesTitle from "./AccessesTitle";
import accessesTable from "./AccessesTable";

export default {
  mixins: [common],

  components: {
    accessesTitle,
    accessesTable
  },

  data() {
    return {
      isLoading: false,
      fetched: false,
      dates: [],
      store: undefined,
      storeName: null,
      accessesFilter: "",
      current: 1,
      pageInput: 1,
      perPage: 100,
      accessesQty: 0
    };
  },

  computed: {
    ...mapGetters(["storeBeingManaged", "accesses", "permissions"]),

    totalPages() {
      return Math.max(1, Math.ceil(this.accessesQty / this.perPage));
    }
  },

  methods: {
    getAccesses(skip) {
      if (isNaN(skip)) {
        skip = (this.current - 1) * this.perPage;
      }
      this.isLoading = true;
      this.$store
        .dispatch("getAccesses", {
          storeId: this.store,
          dates: this.dates,
          skip,
          limit: this.perPage,
          filter: this.accessesFilter
        })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.accessesQty = res.data.payload.accessesQty;
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
          this.fetched = true;
        });
    },

    formatDateStr(d) {
      const date = d ? new Date(d) : new Date();
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },

    updateDates(dates) {
      this.dates = [
        this.formatDateStr(dates ? dates[0] : null),
        this.formatDateStr(dates ? dates[1] : null)
      ];
    },

    updateStore(payload) {
      this.store = payload ? payload.storeId : "";
      this.storeName = payload ? payload.storeName : "Todas as lojas";
      this.current = 1;
      this.pageInput = 1;
      this.getAccesses(0);
    },

    updateAccessesFilter(filter) {
      this.accessesFilter = filter;
      this.current = 1;
      this.pageInput = 1;
      this.getAccesses(0);
    },

    downloadAccessesReport() {
      this.isLoading = true;
      this.$store
        .dispatch("downloadAccessesReport", {
          storeName: this.storeName,
          storeId: this.store,
          dates: this.dates,
          filter: this.accessesFilter
        })
        .then(res => {
          if (res.data.result === "error") {
            this.alertError(res.data);
          } else {
            this.alertReportDowloadMessage();
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    changePage(p) {
      this.pageInput = p;
      const skip = (p - 1) * this.perPage;
      this.getAccesses(skip);
    },

    goToPage() {
      const page = Number(this.pageInput);
      const nextPage = isNaN(page)
        ? 1
        : Math.max(1, Math.min(page, this.totalPages));

      this.current = nextPage;
      this.pageInput = nextPage;
      this.changePage(nextPage);
    }
  },

  mounted() {
    this.updateDates();
  },

  created() {
    if (this.storeBeingManaged && this.storeBeingManaged._id) {
      this.store = this.storeBeingManaged._id;
      this.storeName = this.storeBeingManaged.storeInfo.name;
    }
  }
};
</script>

<style scoped>
#accesses-panel {
  min-height: 100vh;
  background-color: #f8f7fa;
  padding: 1.5rem;
}

.page-inner {
  width: 100%;
}

.content-panel {
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.07), 0 1px 2px rgba(0, 0, 0, 0.04);
  border: 1px solid #f3f4f6;
  padding: 1.5rem;
}

.empty-state {
  padding: 3rem 0;
  color: #9ca3af;
  text-align: center;
  font-size: 0.95rem;
}

.lgpd-notice {
  font-size: 0.8rem;
  color: #9ca3af;
  text-align: center;
  margin-bottom: 1rem;
}

.accesses-count {
  color: #4b5563;
  font-size: 0.9rem;
  font-weight: 600;
  margin-right: auto;
}

.go-to-page {
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: center;
  margin: 12px 0 18px;
}

.go-to-page-controls {
  align-items: center;
  display: flex;
  gap: 8px;
}

.go-to-page-controls .control {
  width: 90px;
}

.accesses-pagination ::v-deep .pagination-link.is-current {
  background-color: #f0512d;
  border-color: #f0512d;
  color: #fff;
}
</style>
